import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { portalApi } from "@/lib/portalApi";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const ccyFmt = (v, ccy = "USD") => {
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(Number(v || 0)); }
  catch { return `${ccy} ${Number(v || 0).toFixed(2)}`; }
};
const todayStr = () => new Date().toISOString().slice(0, 10);
const fmt12 = (t) => {
  if (!t || !/^\d{1,2}:\d{2}/.test(t)) return t;
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
};

const STATUS_TONE = {
  requested: "bg-amber-100 text-amber-700", confirmed: "bg-emerald-100 text-emerald-700",
  pending_payment: "bg-violet-100 text-violet-700", completed: "bg-slate-100 text-slate-600",
  cancelled: "bg-rose-100 text-rose-700", rescheduled: "bg-blue-100 text-blue-700", no_show: "bg-rose-100 text-rose-700",
};

export default function PortalScheduling() {
  const [types, setTypes] = useState([]);
  const [appts, setAppts] = useState([]);
  const [sel, setSel] = useState(null);            // chosen meeting type
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [slotBusy, setSlotBusy] = useState(false);
  const [booking, setBooking] = useState(false);
  const [rs, setRs] = useState(null);             // appointment being rescheduled
  const [rsDate, setRsDate] = useState(todayStr());
  const [rsSlots, setRsSlots] = useState([]);
  const [rsBusy, setRsBusy] = useState(false);
  const [rsSaving, setRsSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [confirmedBanner, setConfirmedBanner] = useState(false);
  const [sp, setSp] = useSearchParams();

  const loadTypes = useCallback(() => { portalApi.get("/portal/scheduling/meeting-types").then((r) => setTypes(r.data.meeting_types || [])).catch(() => setTypes([])); }, []);
  const loadAppts = useCallback(() => { portalApi.get("/portal/scheduling/appointments").then((r) => setAppts(r.data.appointments || [])).catch(() => setAppts([])); }, []);
  useEffect(() => { loadTypes(); loadAppts(); }, [loadTypes, loadAppts]);

  const loadSlots = useCallback(async (mt, d) => {
    if (!mt) return;
    setSlotBusy(true); setSlots([]);
    try {
      const { data } = await portalApi.get(`/portal/scheduling/slots`, { params: { meeting_type_id: mt.id, date: d } });
      setSlots(data.slots || []);
    } catch { setSlots([]); }
    finally { setSlotBusy(false); }
  }, []);

  useEffect(() => { if (sel) loadSlots(sel, date); }, [sel, date, loadSlots]);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 6) { setVerifying(false); toast.info("Still processing — refresh in a moment."); loadAppts(); return; }
    try {
      const { data: st } = await portalApi.get(`/portal/scheduling/checkout/status/${sessionId}`);
      if (st.payment_status === "paid") {
        setVerifying(false); setConfirmedBanner(true); toast.success("Payment received — your meeting is confirmed!");
        loadAppts(); setTimeout(() => setConfirmedBanner(false), 6000); return;
      }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch { setVerifying(false); toast.error("Could not verify payment status."); }
  }, [loadAppts]);

  useEffect(() => {
    const sid = sp.get("session_id");
    if (sid) { setVerifying(true); pollStatus(sid); setSp({}, { replace: true }); }
  }, [sp, setSp, pollStatus]);

  const book = async (slot) => {
    if (!sel) return;
    setBooking(true);
    try {
      const { data } = await portalApi.post("/portal/scheduling/book", {
        meeting_type_id: sel.id, date, start_time: slot, origin_url: window.location.origin,
      });
      if (data.requires_payment && data.checkout_url) {
        window.location.href = data.checkout_url;   // redirect to Stripe
        return;
      }
      toast.success("Appointment booked!");
      setSel(null); setSlots([]); loadAppts();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not book that time"); }
    finally { setBooking(false); }
  };

  const cancel = async (aid) => {
    try { await portalApi.post(`/portal/scheduling/appointments/${aid}/cancel`); toast.success("Appointment cancelled"); loadAppts(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };

  const loadRsSlots = useCallback(async (mtId, d) => {
    if (!mtId) return;
    setRsBusy(true); setRsSlots([]);
    try {
      const { data } = await portalApi.get(`/portal/scheduling/slots`, { params: { meeting_type_id: mtId, date: d } });
      setRsSlots(data.slots || []);
    } catch { setRsSlots([]); }
    finally { setRsBusy(false); }
  }, []);

  const startReschedule = (a) => { setRs(a); setRsDate(a.date || todayStr()); setRsSlots([]); };
  useEffect(() => { if (rs) loadRsSlots(rs.meeting_type_id, rsDate); }, [rs, rsDate, loadRsSlots]);

  const doReschedule = async (slot) => {
    if (!rs) return;
    setRsSaving(true);
    try {
      await portalApi.post(`/portal/scheduling/appointments/${rs.id}/reschedule`, { date: rsDate, start_time: slot });
      toast.success("Appointment rescheduled");
      setRs(null); setRsSlots([]); loadAppts();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reschedule that time"); }
    finally { setRsSaving(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="portal-scheduling">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Schedule a Meeting</h1>
        <p className="text-sm text-slate-500">Book a time with your tax team. Paid consultations are confirmed once payment is complete.</p>
      </div>

      {verifying && <div data-testid="portal-sched-verifying" className="bg-blue-50 border border-blue-200 text-blue-800 rounded-xl px-4 py-3 text-sm flex items-center gap-2"><Icons.Loader2 className="h-4 w-4 animate-spin" />Confirming your payment…</div>}
      {confirmedBanner && <div data-testid="portal-sched-confirmed" className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-3 text-sm flex items-center gap-2"><Icons.CheckCircle2 className="h-4 w-4" />Payment received — your meeting is confirmed.</div>}

      {/* Meeting type picker */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="portal-meeting-types">
        {types.length === 0 && <p className="text-sm text-slate-400">No meeting types are available for self-booking right now.</p>}
        {types.map((mt) => {
          const active = sel?.id === mt.id;
          return (
            <button key={mt.id} onClick={() => setSel(mt)} data-testid={`portal-mt-${mt.id}`}
              className={`text-left rounded-xl border p-4 transition-all ${active ? "border-[#171B2E] ring-2 ring-[#171B2E]/10 bg-white" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: mt.color || "#1f6feb" }} />
                <span className="font-semibold text-slate-900">{mt.name}</span>
              </div>
              <p className="text-xs text-slate-500 mb-2 line-clamp-2">{mt.description || `${mt.duration_min} minute meeting`}</p>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500">{mt.duration_min} min</span>
                {mt.payment_required && (mt.price || 0) > 0
                  ? <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium">{ccyFmt(mt.price)}</span>
                  : <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Free</span>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Slot picker */}
      {sel && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4" data-testid="portal-slot-picker">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-semibold text-slate-800">Pick a time — {sel.name}</h2>
              {sel.payment_required && (sel.price || 0) > 0 && <p className="text-xs text-violet-600 mt-0.5">This is a paid consultation ({ccyFmt(sel.price)}). You'll be taken to secure checkout to confirm.</p>}
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="portal-slot-date"
                  className="inline-flex items-center gap-2 bg-white border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-700 hover:border-[#171B2E] transition-colors">
                  <Icons.CalendarDays className="h-4 w-4 text-slate-400" />
                  {date ? format(parseISO(date), "EEE, MMM d, yyyy") : "Pick a date"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar mode="single" selected={date ? parseISO(date) : undefined}
                  onSelect={(d) => { if (d) setDate(format(d, "yyyy-MM-dd")); }}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus />
              </PopoverContent>
            </Popover>
          </div>
          {slotBusy ? <div className="py-6 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500 inline" /></div>
            : slots.length === 0 ? <p className="text-sm text-slate-400 py-4">No available times on this date. Try another day.</p>
            : <div className="flex flex-wrap gap-2">
                {slots.map((s) => (
                  <button key={s} onClick={() => book(s)} disabled={booking} data-testid={`portal-slot-${s}`}
                    className="px-3.5 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:border-[#171B2E] hover:bg-slate-50 disabled:opacity-50">
                    {fmt12(s)}
                  </button>
                ))}
              </div>}
        </div>
      )}

      {/* Reschedule panel */}
      {rs && (
        <div className="bg-white rounded-xl border border-blue-200 shadow-sm p-5 space-y-4" data-testid="portal-reschedule-panel">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-semibold text-slate-800">Reschedule — {rs.meeting_type_name}</h2>
              <p className="text-xs text-slate-500 mt-0.5">Currently {rs.date} at {fmt12(rs.start_time)} ({rs.timezone}). Your video link stays the same — just pick a new time.</p>
            </div>
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="portal-reschedule-date"
                    className="inline-flex items-center gap-2 bg-white border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-700 hover:border-[#171B2E] transition-colors">
                    <Icons.CalendarDays className="h-4 w-4 text-slate-400" />
                    {rsDate ? format(parseISO(rsDate), "EEE, MMM d, yyyy") : "Pick a date"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar mode="single" selected={rsDate ? parseISO(rsDate) : undefined}
                    onSelect={(d) => { if (d) setRsDate(format(d, "yyyy-MM-dd")); }}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus />
                </PopoverContent>
              </Popover>
              <button onClick={() => { setRs(null); setRsSlots([]); }} data-testid="portal-reschedule-close"
                className="text-sm text-slate-500 hover:text-slate-700 px-2 py-2">Close</button>
            </div>
          </div>
          {rsBusy ? <div className="py-6 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500 inline" /></div>
            : rsSlots.length === 0 ? <p className="text-sm text-slate-400 py-4">No available times on this date. Try another day.</p>
            : <div className="flex flex-wrap gap-2">
                {rsSlots.map((s) => (
                  <button key={s} onClick={() => doReschedule(s)} disabled={rsSaving} data-testid={`portal-reschedule-slot-${s}`}
                    className="px-3.5 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:border-blue-500 hover:bg-blue-50 disabled:opacity-50">
                    {fmt12(s)}
                  </button>
                ))}
              </div>}
        </div>
      )}

      {/* My appointments */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="portal-my-appointments">
        <h2 className="font-semibold text-slate-800 mb-3">My Appointments</h2>
        {appts.length === 0 ? <p className="text-sm text-slate-400">You have no appointments yet.</p>
          : <div className="divide-y divide-slate-100">
              {appts.map((a) => {
                const [lbl, tone] = [a.status.replace(/_/g, " "), STATUS_TONE[a.status] || "bg-slate-100 text-slate-600"];
                const upcoming = a.status !== "cancelled" && a.status !== "completed";
                return (
                  <div key={a.id} className="py-3 flex items-center justify-between gap-3" data-testid={`portal-appt-${a.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{a.meeting_type_name}</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${tone}`}>{lbl}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{a.date} at {fmt12(a.start_time)} ({a.timezone}) · {a.duration_min} min</p>
                      {a.previous_start_time && (
                        <p className="text-[11px] text-blue-600 mt-1 inline-flex items-center gap-1" data-testid={`portal-appt-reschedule-note-${a.id}`}>
                          <Icons.CalendarClock className="h-3 w-3" />
                          Rescheduled from <span className="line-through opacity-70">{a.previous_date} at {fmt12(a.previous_start_time)}</span>
                          {a.rescheduled_at && <span className="text-blue-400">· {new Date(a.rescheduled_at).toLocaleDateString()}</span>}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {a.zoom?.join_url && a.status === "confirmed" &&
                        <a href={a.zoom.join_url} target="_blank" rel="noreferrer" data-testid={`portal-appt-join-${a.id}`}
                          className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5 flex items-center gap-1"><Icons.Video className="h-4 w-4" />Join</a>}
                      {upcoming && <button onClick={() => startReschedule(a)} data-testid={`portal-appt-reschedule-${a.id}`}
                        className="text-sm text-blue-600 hover:text-blue-700 px-2 py-1.5">Reschedule</button>}
                      {upcoming && <button onClick={() => cancel(a.id)} data-testid={`portal-appt-cancel-${a.id}`}
                        className="text-sm text-rose-600 hover:text-rose-700 px-2 py-1.5">Cancel</button>}
                    </div>
                  </div>
                );
              })}
            </div>}
      </div>
    </div>
  );
}
